import type { ReactNode } from 'react';
import { ScrollView } from 'react-native';
import { YStack } from 'tamagui';
import { FormSection, SectionLabel } from '@kiips/ui';
import type {
  Attachment,
  AttachmentAnchor,
  AttachmentLabel,
  EntityAuthorshipFields,
  Trip,
} from '@my-fullstack-app/itinerary-types';
import { AiBadge } from '../assistant/AiBadge';
import { CostItemsEditor } from './CostItemsEditor';
import { AttachmentsSection } from './AttachmentsSection';
import { DeleteRowButton } from './DeleteRowButton';
import { NotesEditorBlock } from './NotesEditorBlock';
import { TagPickerRow } from './TagPickerRow';
import { ResponsivePage } from '../design/ResponsivePage';
import { type CostBearing, entityLineItems } from '../../lib/cost';
import { useItineraryStore } from '../../store/itinerary-store';
import { trpc } from '../../lib/trpc';

/**
 * Shared base edit view for the four itinerary detail screens — Visit
 * (place), Stay, Journey, Placeholder. Owns the page chrome and the
 * canonical section order:
 *
 *   Base Info → Tags → Cost → Notes → Files → Delete
 *
 * Each screen composes its own entity-specific Base Info via `children`;
 * everything below it is identical across all four. Tags / Cost / Files
 * hide for read-only viewers when they have no content; Delete is
 * editor-only. Notes (tagsMap / notesMap wiring) are pulled straight
 * from the store here so callers only supply the entity id.
 */
interface EntityEditViewProps {
  /** Hero element rendered above the form — caller composes <HeroStrip />. */
  hero: ReactNode;
  /** Trip the entity belongs to. */
  trip: Trip;
  /** Entity id — keys notes + tags in the store. */
  entityId: string;
  /** Whether the viewer can edit this trip. */
  canEdit: boolean;
  /** Base Info — caller-composed, entity-specific fields. */
  children: ReactNode;
  /** Cost-bearer for the shared per-line-item cost editor. */
  costBearer: CostBearing;
  /** Placeholder shown in the notes editor while empty. */
  notePlaceholder?: string | undefined;
  /** Attachments anchored to this entity (caller filters via getAttachmentsFor). */
  attachments: Attachment[];
  /** Anchor applied to new uploads. */
  attachmentAnchor: AttachmentAnchor;
  /** Default label for new uploads. */
  attachmentDefaultLabel?: AttachmentLabel | undefined;
  /** Single-line hint shown when there are no files. */
  attachmentEmptyHint?: string | undefined;
  /** Visible label for the destructive footer button. */
  deleteLabel: string;
  /** Title for the delete confirmation dialog. */
  deleteConfirmTitle: string;
  /** Message for the delete confirmation dialog. */
  deleteConfirmMessage: string;
  /** Persist the removal + navigate away. */
  onDelete: () => void;
  /** ADR-0084 — when the entity carries assistant authorship, render
   *  a small "AI" badge under the hero. Caller passes the entity's
   *  authorship fields directly; the badge handles its own
   *  visibility (hides when `userPrefs.hideAiBadges` is on or the
   *  badge has been dismissed for this entity id). */
  authorship?: EntityAuthorshipFields | undefined;
}

export function EntityEditView({
  hero,
  trip,
  entityId,
  canEdit,
  children,
  costBearer,
  notePlaceholder,
  attachments,
  attachmentAnchor,
  attachmentDefaultLabel,
  attachmentEmptyHint,
  deleteLabel,
  deleteConfirmTitle,
  deleteConfirmMessage,
  onDelete,
  authorship,
}: EntityEditViewProps) {
  const tag = useItineraryStore((s) => s.tagsMap[entityId]);
  const note = useItineraryStore((s) => s.notesMap[entityId]);
  const setNote = useItineraryStore((s) => s.setNote);
  const setTag = useItineraryStore((s) => s.setTag);

  // Read-only viewers see a section only when it carries content —
  // empty pickers / "Add" affordances would imply edit access.
  const showTags = canEdit || tag != null;
  const showCost = canEdit || entityLineItems(costBearer).length > 0;
  const showFiles = canEdit || attachments.length > 0;
  // ADR-0081 / ADR-0083: surface extract actions on entity-anchored
  // attachments too — the trip-level Vault isn't the only place a
  // passport / booking confirmation might live. Disabled when files
  // section won't render.
  //
  // KEEP staleTime + refetchOnWindowFocus IN SYNC with the
  // TripReviewView observer (apps/mobile/src/components/design/TripReviewView.tsx).
  // Each React Query observer evaluates those options independently
  // against the shared cache by key, so divergent values trigger
  // refetches on different clocks; when both views co-mount during
  // entity-edit navigation from the review pane, the eagerer
  // observer can spuriously invalidate the cached quota and force
  // an extra round-trip the trip-level view explicitly opted out of.
  // Drift here means surprising refetch races, not data corruption,
  // but the surprise is the bug.
  const quotaQuery = trpc.ai.quotaStatus.useQuery(undefined, {
    enabled: showFiles && canEdit,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  return (
    <ResponsivePage showTopBar={false}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 80 }}
        keyboardShouldPersistTaps="handled"
      >
        {hero}

        <YStack paddingHorizontal={20} paddingTop={22} gap={12}>
          {/* ADR-0084 — assistant attribution chip. Renders nothing
              when the entity wasn't AI-touched or the user has
              dismissed badges. */}
          {authorship ? (
            <YStack alignItems="flex-start">
              <AiBadge entityId={entityId} entity={authorship} />
            </YStack>
          ) : null}

          {/* Base Info — entity-specific */}
          {children}

          {/* Tags */}
          {showTags ? (
            <>
              <SectionLabel marginTop={6}>Tags</SectionLabel>
              <TagPickerRow
                selected={tag}
                onChange={(next) => setTag(trip.id, entityId, next)}
                canEdit={canEdit}
              />
            </>
          ) : null}

          {/* Cost */}
          {showCost ? (
            <>
              <SectionLabel marginTop={6}>Cost</SectionLabel>
              <FormSection>
                <CostItemsEditor trip={trip} bearer={costBearer} canEdit={canEdit} />
              </FormSection>
            </>
          ) : null}

          {/* Notes */}
          <NotesEditorBlock
            content={note}
            onSave={(next) => setNote(trip.id, entityId, next)}
            canEdit={canEdit}
            placeholder={notePlaceholder}
          />

          {/* Files */}
          {showFiles ? (
            <AttachmentsSection
              tripId={trip.id}
              attachments={attachments}
              anchor={attachmentAnchor}
              defaultLabel={attachmentDefaultLabel}
              canEdit={canEdit}
              title="Files"
              emptyHint={attachmentEmptyHint}
              parseQuotaStatus={quotaQuery.data?.parse}
              extractQuotaStatus={quotaQuery.data?.extract}
            />
          ) : null}

          {/* Delete */}
          {canEdit ? (
            <YStack marginTop={16}>
              <DeleteRowButton
                label={deleteLabel}
                confirmTitle={deleteConfirmTitle}
                confirmMessage={deleteConfirmMessage}
                onConfirm={onDelete}
              />
            </YStack>
          ) : null}
        </YStack>
      </ScrollView>
    </ResponsivePage>
  );
}
